import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LoaderCircle, X } from 'lucide-react';
import type {
  Location,
  SellerOrder,
  SellerOrderLocationOption,
  SellerOrderProductOption,
  SellerOrderRequest,
} from '../services/api.types';
import { fetchUserLocations } from '../services/location.api';
import { createSellerOrderLocation, fetchSellerOrderLocationOptions } from '../services/seller-order.api';
import { getUserId } from '../services/auth';
import { useTranslations } from '../translations';
import { useNotification } from './NotificationProvider';
import DateTimePicker from './DateTimePicker';
import LocationAutocomplete from './LocationAutocomplete';
import ThemedSelect from './ThemedSelect';
import UnitTypeSelect from './UnitTypeSelect';
import { formatUnitType, normalizeUnitType } from '../utils/unitType';
import './SellerOrderFormModal.css';

const localDateTimeValue = (date = new Date()) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

const toInstant = (value: string) => new Date(value).toISOString();
const fromInstant = (value: string | null) => value ? localDateTimeValue(new Date(value)) : null;
const withPreservedTime = (date: string, currentValue: string | null, fallbackTime: string) => {
  if (!date) return '';
  const currentTime = currentValue?.split('T')[1];
  return `${date}T${currentTime || fallbackTime}`;
};

const money = (value: number) => new Intl.NumberFormat('ro-RO', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(value || 0);

const SWIPE_DISMISS_RATIO = 0.9;

type SellerOrderFieldName =
  | 'productId'
  | 'deliveryLocation'
  | 'deliveryPointName'
  | 'customerName'
  | 'quantity'
  | 'unitType'
  | 'unitPrice'
  | 'orderDate'
  | 'deliveryDate';

type SellerOrderFieldErrors = Partial<Record<SellerOrderFieldName, string>>;

export const createEmptySellerOrderForm = (
  overrides: Partial<SellerOrderRequest> = {},
): SellerOrderRequest => ({
  productId: '',
  orderLocationId: null,
  deliveryLocationId: '',
  deliveryPointName: '',
  deliveryAddressDetails: '',
  customerName: '',
  customerPhone: '',
  quantity: 1,
  unitType: '',
  unitPrice: 0,
  remarks: '',
  orderDate: localDateTimeValue(),
  deliveryDate: null,
  ...overrides,
});

export const createSellerOrderFormFromOrder = (order: SellerOrder): SellerOrderRequest => ({
  productId: order.productId || '',
  orderLocationId: order.orderLocationId,
  deliveryLocationId: order.deliveryLocationId,
  deliveryPointName: order.deliveryPointName,
  deliveryAddressDetails: order.deliveryAddressDetails || '',
  customerName: order.customerName,
  customerPhone: order.customerPhone || '',
  quantity: order.quantity,
  unitType: order.unitType,
  unitPrice: order.unitPrice,
  remarks: order.remarks || '',
  orderDate: fromInstant(order.orderDate) || localDateTimeValue(),
  deliveryDate: fromInstant(order.deliveryDate),
});

interface SellerOrderFormModalProps {
  title: string;
  products: SellerOrderProductOption[];
  initialForm: SellerOrderRequest;
  initialDeliveryLocation: Location | null;
  onClose: () => void;
  onSubmit: (request: SellerOrderRequest) => Promise<void>;
}

const SellerOrderFormModal: React.FC<SellerOrderFormModalProps> = ({
  title,
  products,
  initialForm,
  initialDeliveryLocation,
  onClose,
  onSubmit,
}) => {
  const t = useTranslations();
  const copy = t.orders;
  const { showNotification } = useNotification();
  // New orders only for active sale listings; an order being edited keeps its own listing even if it has expired since.
  const orderableProducts = products.filter(
    (product) => product.status === 'ACTIVE' || product.id === initialForm.productId,
  );
  const [form, setForm] = useState(() => ({
    ...initialForm,
    deliveryPointName: initialForm.deliveryPointName
      || initialDeliveryLocation?.diacriticsName
      || initialDeliveryLocation?.name
      || '',
    unitType: normalizeUnitType(
      initialForm.unitType
        || products.find((product) => product.id === initialForm.productId)?.unitType
        || '',
    ),
  }));
  const [quantityInput, setQuantityInput] = useState(String(initialForm.quantity));
  const [unitPriceInput, setUnitPriceInput] = useState(String(initialForm.unitPrice));
  const [deliveryLocation, setDeliveryLocation] = useState<Location | null>(initialDeliveryLocation);
  const [locationSuggestions, setLocationSuggestions] = useState<Location[]>(() => products.flatMap((product) => (
    product.productLocation ? [product.productLocation] : []
  )));
  const [orderLocations, setOrderLocations] = useState<SellerOrderLocationOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [showDeliveryPointModal, setShowDeliveryPointModal] = useState(false);
  const [deliveryPointName, setDeliveryPointName] = useState('');
  const [deliveryPointAddress, setDeliveryPointAddress] = useState('');
  const [savingDeliveryPoint, setSavingDeliveryPoint] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [deliveryPointValidationAttempted, setDeliveryPointValidationAttempted] = useState(false);
  const modalRef = useRef<HTMLElement>(null);
  const dragStartYRef = useRef(0);
  const dragOffsetRef = useRef(0);
  const dragPointerIdRef = useRef<number | null>(null);
  const [dragging, setDragging] = useState(false);

  const setDragOffset = (offset: number) => {
    dragOffsetRef.current = offset;
    modalRef.current?.style.setProperty('--seller-order-drag-y', `${offset}px`);
  };

  const finishDrag = (event: React.PointerEvent<HTMLElement>, cancelled = false) => {
    if (dragPointerIdRef.current !== event.pointerId) return;

    const modalHeight = modalRef.current?.offsetHeight || window.innerHeight;
    const shouldClose = !cancelled && !saving
      && dragOffsetRef.current >= modalHeight * SWIPE_DISMISS_RATIO;

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragPointerIdRef.current = null;
    setDragging(false);

    if (shouldClose) {
      onClose();
      return;
    }
    setDragOffset(0);
  };

  const startDrag = (event: React.PointerEvent<HTMLElement>) => {
    if (saving || event.pointerType === 'mouse' || (event.target as Element).closest('button')) return;
    dragPointerIdRef.current = event.pointerId;
    dragStartYRef.current = event.clientY;
    setDragOffset(0);
    setDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveDrag = (event: React.PointerEvent<HTMLElement>) => {
    if (dragPointerIdRef.current !== event.pointerId) return;
    setDragOffset(Math.max(0, event.clientY - dragStartYRef.current));
  };

  const getFieldErrors = (): SellerOrderFieldErrors => {
    const errors: SellerOrderFieldErrors = {};
    const parsedQuantity = Number(quantityInput);
    const parsedUnitPrice = Number(unitPriceInput);

    if (!form.productId) errors.productId = copy.form.fieldRequired;
    if (!deliveryLocation) errors.deliveryLocation = copy.form.fieldRequired;
    if (!form.deliveryPointName.trim()) errors.deliveryPointName = copy.form.fieldRequired;
    if (!form.customerName.trim()) errors.customerName = copy.form.fieldRequired;
    if (!quantityInput.trim()) errors.quantity = copy.form.fieldRequired;
    else if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      errors.quantity = copy.form.invalidQuantity;
    }
    if (!form.unitType.trim()) errors.unitType = copy.form.fieldRequired;
    if (!unitPriceInput.trim()) errors.unitPrice = copy.form.fieldRequired;
    else if (!Number.isFinite(parsedUnitPrice) || parsedUnitPrice < 0) {
      errors.unitPrice = copy.form.invalidUnitPrice;
    }
    if (!form.orderDate) errors.orderDate = copy.form.fieldRequired;
    if (form.deliveryDate && form.orderDate
      && form.orderDate.slice(0, 10) > form.deliveryDate.slice(0, 10)) {
      errors.deliveryDate = copy.form.invalidDateRange;
    }

    return errors;
  };

  const fieldErrors = validationAttempted ? getFieldErrors() : {};
  const fieldErrorId = (field: SellerOrderFieldName) => `seller-order-${field}-error`;
  const fieldClassName = (field: SellerOrderFieldName, wide = false) => (
    `seller-order-field${wide ? ' seller-order-field-wide' : ''}${fieldErrors[field] ? ' seller-order-field-invalid' : ''}`
  );
  const requiredLabel = (label: string) => (
    <span className="seller-order-field-label">
      {label}<span className="seller-order-required-marker" aria-hidden="true">*</span>
    </span>
  );
  const fieldError = (field: SellerOrderFieldName) => fieldErrors[field] ? (
    <small id={fieldErrorId(field)} className="seller-order-field-error">
      {fieldErrors[field]}
    </small>
  ) : null;

  useEffect(() => {
    let active = true;
    const userId = getUserId();
    const userLocationsRequest = userId
      ? fetchUserLocations(userId, 1, 100)
        .then((response) => response.content)
        .catch((error) => {
          console.error('Could not load saved user locations', error);
          return [];
        })
      : Promise.resolve([] as Location[]);
    const orderLocationsRequest = userId ? fetchSellerOrderLocationOptions()
      .then((locations) => {
        const activeLocations = locations.filter((location) => location.active);
        if (active) setOrderLocations(activeLocations);
        return activeLocations.map((location) => ({
        id: location.locationId,
        name: location.locationName,
        diacriticsName: location.locationName,
        county: { id: '', name: location.countyName },
        coordinates: { latitude: 0, longitude: 0 },
        defaultLocation: false,
        } satisfies Location));
      })
      .catch((error) => {
        console.error('Could not load previous order locations', error);
        return [];
      }) : Promise.resolve([] as Location[]);

    Promise.all([userLocationsRequest, orderLocationsRequest])
      .then(([userLocations, orderLocations]) => {
        if (!active) return;
        const productLocations = products.flatMap((product) => (
          product.productLocation ? [product.productLocation] : []
        ));
        const mergedLocations = [...userLocations, ...orderLocations, ...productLocations].filter(
          (location, index, all) => all.findIndex((candidate) => candidate.id === location.id) === index,
        );
        setLocationSuggestions((current) => (
          current.length === 0 && mergedLocations.length === 0 ? current : mergedLocations
        ));
      });
    return () => {
      active = false;
    };
  }, [products]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (showDeliveryPointModal && !savingDeliveryPoint) {
        setShowDeliveryPointModal(false);
        return;
      }
      if (!saving) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, saving, savingDeliveryPoint, showDeliveryPointModal]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const createDeliveryPoint = async (event: React.FormEvent) => {
    event.preventDefault();
    setDeliveryPointValidationAttempted(true);
    if (!deliveryLocation || !deliveryPointName.trim()) {
      showNotification(copy.form.deliveryPointRequiredError, 'error');
      return;
    }
    setSavingDeliveryPoint(true);
    try {
      const created = await createSellerOrderLocation({
        locationId: deliveryLocation.id,
        name: deliveryPointName.trim(),
        addressDetails: deliveryPointAddress.trim(),
      });
      const option: SellerOrderLocationOption = {
        id: created.id,
        locationId: created.locationId,
        locationName: created.locationName,
        countyName: created.countyName,
        name: created.name,
        addressDetails: created.addressDetails,
        active: created.active,
        orderCount: created.orderCount,
      };
      setOrderLocations((current) => [
        option,
        ...current.filter((location) => location.id !== option.id),
      ]);
      setForm((current) => ({
        ...current,
        orderLocationId: option.id,
        deliveryLocationId: option.locationId,
        deliveryPointName: option.name,
        deliveryAddressDetails: option.addressDetails || '',
      }));
      setShowDeliveryPointModal(false);
      setDeliveryPointValidationAttempted(false);
      showNotification(copy.form.deliveryPointCreated, 'success');
    } catch (error) {
      console.error('Could not create delivery point', error);
      showNotification(copy.form.deliveryPointCreateError, 'error');
    } finally {
      setSavingDeliveryPoint(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setValidationAttempted(true);
    const parsedQuantity = Number(quantityInput);
    const parsedUnitPrice = Number(unitPriceInput);
    const errors = getFieldErrors();
    if (Object.keys(errors).length > 0) {
      showNotification(errors.deliveryDate || copy.form.requiredError, 'error');
      const formElement = event.currentTarget;
      window.requestAnimationFrame(() => {
        const firstInvalidControl = formElement.querySelector<HTMLElement>('[aria-invalid="true"]');
        firstInvalidControl?.focus();
      });
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        ...form,
        deliveryLocationId: deliveryLocation!.id,
        deliveryPointName: form.deliveryPointName.trim(),
        deliveryAddressDetails: form.deliveryAddressDetails?.trim(),
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone?.trim(),
        quantity: parsedQuantity,
        unitType: form.unitType.trim(),
        unitPrice: parsedUnitPrice,
        remarks: form.remarks?.trim(),
        orderDate: toInstant(form.orderDate),
        deliveryDate: form.deliveryDate ? toInstant(form.deliveryDate) : null,
      });
    } finally {
      setSaving(false);
    }
  };

  return createPortal((
    <div
      className="seller-order-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}
    >
      <section
        ref={modalRef}
        className={`seller-order-modal${dragging ? ' seller-order-modal-dragging' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="seller-order-form-title"
      >
        <header
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={(event) => finishDrag(event)}
          onPointerCancel={(event) => finishDrag(event, true)}
        >
          <h2 id="seller-order-form-title">{title}</h2>
          <button type="button" onClick={onClose} disabled={saving} aria-label={t.common.close}>
            <X size={20} aria-hidden />
          </button>
        </header>
        <form onSubmit={submit} noValidate>
          <div className="seller-order-form-fields">
          <div className={fieldClassName('productId', true)}>
            {requiredLabel(copy.form.product)}
            <ThemedSelect
              value={form.productId}
              onChange={(productId) => {
                const product = products.find((item) => item.id === productId);
                const productLocation = product?.productLocation ?? null;
                setDeliveryLocation(productLocation);
                setUnitPriceInput(product ? String(product.unitPrice) : '');
                setForm((current) => ({
                  ...current,
                  productId,
                  orderLocationId: null,
                  unitPrice: product?.unitPrice ?? 0,
                  unitType: product ? normalizeUnitType(product.unitType) : '',
                  deliveryLocationId: productLocation?.id || '',
                  deliveryPointName: productLocation?.diacriticsName || productLocation?.name || '',
                  deliveryAddressDetails: '',
                }));
              }}
              ariaLabel={copy.form.product}
              ariaDescribedBy={fieldErrors.productId ? fieldErrorId('productId') : undefined}
              invalid={Boolean(fieldErrors.productId)}
              options={[
                { value: '', label: copy.form.chooseProduct },
                ...orderableProducts.map((product) => ({
                  value: product.id,
                  label: `${product.title} · ${money(product.unitPrice)} lei/${formatUnitType(product.unitType)}`
                    + (product.status === 'ACTIVE' ? '' : ` (${copy.form.productNotActive})`),
                })),
              ]}
            />
            {fieldError('productId')}
            {orderableProducts.length === 0 && <p className="seller-order-field-hint">{copy.form.noActiveProducts}</p>}
          </div>
          <label className={fieldClassName('deliveryLocation', true)}>
            {requiredLabel(copy.form.deliveryLocation)}
            <LocationAutocomplete
              value={deliveryLocation}
              onChange={(location) => {
                setDeliveryLocation(location);
                setForm((current) => ({
                  ...current,
                  orderLocationId: null,
                  deliveryLocationId: location?.id || '',
                  deliveryPointName: location?.diacriticsName || location?.name || '',
                  deliveryAddressDetails: '',
                }));
              }}
              placeholder={copy.form.locationPlaceholder}
              ariaLabel={copy.form.deliveryLocation}
              ariaDescribedBy={fieldErrors.deliveryLocation ? fieldErrorId('deliveryLocation') : undefined}
              invalid={Boolean(fieldErrors.deliveryLocation)}
              required
              suggestions={locationSuggestions}
            />
            {fieldError('deliveryLocation')}
          </label>
          {deliveryLocation && (
            <>
              <div className={fieldClassName('deliveryPointName', true)}>
                {requiredLabel(copy.form.deliveryPoint)}
                <ThemedSelect
                  value={form.orderLocationId || ''}
                  onChange={(value) => {
                    if (value === '__new__') {
                      setDeliveryPointName('');
                      setDeliveryPointAddress('');
                      setDeliveryPointValidationAttempted(false);
                      setShowDeliveryPointModal(true);
                      return;
                    }
                    const selected = orderLocations.find((location) => location.id === value);
                    if (!selected) return;
                    setForm((current) => ({
                      ...current,
                      orderLocationId: selected.id,
                      deliveryPointName: selected.name,
                      deliveryAddressDetails: selected.addressDetails || '',
                    }));
                  }}
                  ariaLabel={copy.form.deliveryPoint}
                  ariaDescribedBy={fieldErrors.deliveryPointName ? fieldErrorId('deliveryPointName') : undefined}
                  invalid={Boolean(fieldErrors.deliveryPointName)}
                  options={[
                    { value: '', label: copy.form.chooseDeliveryPoint },
                    ...orderLocations
                      .filter((location) => location.locationId === deliveryLocation.id)
                      .map((location) => ({
                        value: location.id,
                        label: location.addressDetails
                          ? `${location.name} · ${location.addressDetails}`
                          : location.name,
                      })),
                    ...(form.orderLocationId
                      && !orderLocations.some((location) => location.id === form.orderLocationId)
                      ? [{ value: form.orderLocationId, label: form.deliveryPointName }]
                      : []),
                    { value: '__new__', label: copy.form.newDeliveryPoint, action: true },
                  ]}
                />
                {fieldError('deliveryPointName')}
              </div>
            </>
          )}
          <label className={fieldClassName('customerName')}>
            {requiredLabel(copy.form.customerName)}
            <input
              value={form.customerName}
              maxLength={50}
              required
              aria-label={copy.form.customerName}
              aria-invalid={Boolean(fieldErrors.customerName)}
              aria-describedby={fieldErrors.customerName ? fieldErrorId('customerName') : undefined}
              onChange={(event) => setForm({ ...form, customerName: event.target.value })}
            />
            {fieldError('customerName')}
          </label>
          <label className="seller-order-field">
            <span>{copy.form.customerPhone}</span>
            <input type="tel" value={form.customerPhone || ''} maxLength={50} onChange={(event) => setForm({ ...form, customerPhone: event.target.value })} />
          </label>
          <label className={fieldClassName('quantity')}>
            {requiredLabel(copy.form.quantity)}
            <input
              type="number"
              min="0.001"
              step="0.001"
              required
              value={quantityInput}
              aria-label={copy.form.quantity}
              aria-invalid={Boolean(fieldErrors.quantity)}
              aria-describedby={fieldErrors.quantity ? fieldErrorId('quantity') : undefined}
              onChange={(event) => setQuantityInput(event.target.value)}
            />
            {fieldError('quantity')}
          </label>
          <div className={fieldClassName('unitType')}>
            {requiredLabel(copy.form.unit)}
            <UnitTypeSelect
              value={form.unitType}
              onChange={(unitType) => setForm({ ...form, unitType })}
              ariaLabel={copy.form.unit}
              ariaDescribedBy={fieldErrors.unitType ? fieldErrorId('unitType') : undefined}
              invalid={Boolean(fieldErrors.unitType)}
            />
            {fieldError('unitType')}
          </div>
          <label className={fieldClassName('unitPrice')}>
            {requiredLabel(`${copy.form.unitPrice} (lei)`)}
            <input
              type="number"
              min="0"
              step="0.01"
              required
              value={unitPriceInput}
              aria-label={`${copy.form.unitPrice} (lei)`}
              aria-invalid={Boolean(fieldErrors.unitPrice)}
              aria-describedby={fieldErrors.unitPrice ? fieldErrorId('unitPrice') : undefined}
              onChange={(event) => setUnitPriceInput(event.target.value)}
            />
            {fieldError('unitPrice')}
          </label>
          <div className={fieldClassName('orderDate')}>
            {requiredLabel(copy.form.orderDate)}
            <DateTimePicker
              value={form.orderDate}
              onChange={(orderDate) => setForm({
                ...form,
                orderDate: withPreservedTime(orderDate, form.orderDate, localDateTimeValue().slice(11)),
              })}
              ariaLabel={copy.form.orderDate}
              clearLabel={copy.form.clearOrderDate}
              ariaDescribedBy={fieldErrors.orderDate ? fieldErrorId('orderDate') : undefined}
              invalid={Boolean(fieldErrors.orderDate)}
              required
              dateOnly
            />
            {fieldError('orderDate')}
          </div>
          <div className={fieldClassName('deliveryDate')}>
            <span>{copy.form.deliveryDate}</span>
            <DateTimePicker
              value={form.deliveryDate || ''}
              onChange={(deliveryDate) => setForm({
                ...form,
                deliveryDate: deliveryDate
                  ? `${deliveryDate}T23:59:59.999`
                  : null,
              })}
              ariaLabel={copy.form.deliveryDate}
              clearLabel={copy.form.clearDeliveryDate}
              ariaDescribedBy={fieldErrors.deliveryDate ? fieldErrorId('deliveryDate') : undefined}
              invalid={Boolean(fieldErrors.deliveryDate)}
              dateOnly
            />
            {fieldError('deliveryDate')}
          </div>
          <label className="seller-order-field seller-order-field-wide">
            <span>{copy.form.remarks}</span>
            <textarea
              value={form.remarks || ''}
              maxLength={500}
              rows={3}
              placeholder={copy.form.remarksPlaceholder}
              aria-describedby="seller-order-remarks-count"
              onChange={(event) => setForm({ ...form, remarks: event.target.value })}
            />
            <span id="seller-order-remarks-count" className="seller-order-character-count" aria-live="polite">
              {(form.remarks || '').length}/500
            </span>
          </label>
          <div className="seller-order-form-total seller-order-field-wide">
            <span>{copy.form.calculatedTotal}</span>
            <strong>{money(Number(quantityInput) * Number(unitPriceInput))} lei</strong>
          </div>
          </div>
          <footer className="seller-order-form-actions seller-order-field-wide">
            <button type="button" onClick={onClose} disabled={saving}>{t.common.cancel}</button>
            <button type="submit" className="seller-order-primary-button" disabled={saving}>
              {saving && <LoaderCircle className="seller-order-saving-spinner" size={17} aria-hidden />}
              {saving ? copy.form.saving : t.common.save}
            </button>
          </footer>
        </form>
      </section>
      {showDeliveryPointModal && deliveryLocation && createPortal((
        <div
          className="seller-order-modal-backdrop seller-order-delivery-point-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !savingDeliveryPoint) {
              setShowDeliveryPointModal(false);
            }
          }}
        >
          <section
            className="seller-order-modal seller-order-delivery-point-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="seller-order-delivery-point-title"
          >
            <header>
              <h2 id="seller-order-delivery-point-title">{copy.form.createDeliveryPointTitle}</h2>
              <button
                type="button"
                onClick={() => setShowDeliveryPointModal(false)}
                disabled={savingDeliveryPoint}
                aria-label={t.common.close}
              >
                <X size={20} aria-hidden />
              </button>
            </header>
            <form onSubmit={createDeliveryPoint} noValidate>
              <div className="seller-order-form-fields">
              <div className="seller-order-field seller-order-field-wide">
                {requiredLabel(copy.form.deliveryLocation)}
                <div className="seller-order-delivery-point-locality">
                  {deliveryLocation.diacriticsName || deliveryLocation.name}, {deliveryLocation.county.name}
                </div>
              </div>
              <label className={`seller-order-field seller-order-field-wide${deliveryPointValidationAttempted && !deliveryPointName.trim() ? ' seller-order-field-invalid' : ''}`}>
                {requiredLabel(copy.form.deliveryPointName)}
                <input
                  autoFocus
                  value={deliveryPointName}
                  maxLength={100}
                  required
                  aria-label={copy.form.deliveryPointName}
                  aria-invalid={deliveryPointValidationAttempted && !deliveryPointName.trim()}
                  aria-describedby={deliveryPointValidationAttempted && !deliveryPointName.trim()
                    ? 'seller-order-delivery-point-name-error'
                    : undefined}
                  placeholder={copy.form.deliveryPointNamePlaceholder}
                  onChange={(event) => setDeliveryPointName(event.target.value)}
                />
                {deliveryPointValidationAttempted && !deliveryPointName.trim() && (
                  <small id="seller-order-delivery-point-name-error" className="seller-order-field-error">
                    {copy.form.fieldRequired}
                  </small>
                )}
              </label>
              <label className="seller-order-field seller-order-field-wide">
                <span>{copy.form.deliveryAddressDetails}</span>
                <input
                  value={deliveryPointAddress}
                  maxLength={255}
                  placeholder={copy.form.deliveryAddressPlaceholder}
                  onChange={(event) => setDeliveryPointAddress(event.target.value)}
                />
              </label>
              </div>
              <footer className="seller-order-form-actions seller-order-field-wide">
                <button
                  type="button"
                  onClick={() => setShowDeliveryPointModal(false)}
                  disabled={savingDeliveryPoint}
                >
                  {t.common.cancel}
                </button>
                <button type="submit" className="seller-order-primary-button" disabled={savingDeliveryPoint}>
                  {savingDeliveryPoint && <LoaderCircle className="seller-order-saving-spinner" size={17} aria-hidden />}
                  {savingDeliveryPoint ? copy.form.savingDeliveryPoint : copy.form.addDeliveryPoint}
                </button>
              </footer>
            </form>
          </section>
        </div>
      ), document.body)}
    </div>
  ), document.body);
};

export default SellerOrderFormModal;
